
import "styles/questionnaire/hmrcAuth.less";
import { useEffect, useState } from "react";

import { Input, message } from "antd";
import { useAppSelector } from "redux/hooks";
import { RootState } from "redux/store";
import { useInitiateHMRCLoginMutation } from "services/hmrc-integrations";
import { IUser } from "types/auth";
import { logError } from "utils/sentry";
import { isUTRValid, normalizeUTR } from "utils/validation";

type HmrcAuthProps = {
    user: IUser;
};

function HmrcAuth({ user }: HmrcAuthProps) {
    const [utr, setUtr] = useState("");
    const [isEditing, setIsEditing] = useState(true);
    const [startHmrcAuth, { isLoading: isAuthLoading, error: authError }] = useInitiateHMRCLoginMutation();
    const normalizedUTR = normalizeUTR(utr);
    const isValidUtr = isUTRValid(normalizedUTR);
    const isDisabled = !isValidUtr || isAuthLoading;
    const { currentDocument } = useAppSelector(
        (state: RootState) => ({
            currentDocument: state.document.currentDocument,
        })
    );
    const handleConfirm = async () => {
        if (!isValidUtr) return;

        const documentId = currentDocument?.id ?? "";
        if (!documentId) {
            message.error("Document ID is missing.");
            return;
        }

        try {
            const response = await startHmrcAuth({ utr, documentId }).unwrap();
            if (response?.authUrl) {
                window.location.href = response.authUrl;
            }
        } catch (err: any) {
            logError("Error starting HMRC auth:", err);
            message.error("Cannot connect to HMRC, try again later");
        }
    };

    useEffect(() => {
        if (user?.utr) {
            setUtr(user.utr);
            setIsEditing(false);
        }
    }, [user?.utr]);

    return (
        <>
            <div className="hmrc-auth__title">Login to HMRC</div>
            <div className="hmrc-auth__container">
                <div className="hmrc-auth__subtitle">
        We’ll use your UTR to match your records with HMRC. Please make sure your UTR number is correct.
                </div>
                <Input
                    id="utr"
                    value={utr}
                    readOnly={!isEditing}
                    allowClear={isEditing}
                    autoFocus={isEditing}
                    placeholder="Enter UTR"
                    onClick={() => !isEditing && setIsEditing(true)}
                    onChange={e => setUtr(e.target.value.toUpperCase())}
                    onBlur={() => setIsEditing(false)}
                />
            </div>

            {utr && !isValidUtr && (
                <div className="hmrc-auth__error-message-text">Please enter a valid UTR</div>
            )}
            <button
                className={isDisabled ? "shared__primary-button-light w100 mt20" : "shared__primary-button w100 mt20"}
                disabled={isDisabled}
                onClick={handleConfirm}
            >
                Continue to HMRC
            </button>
            {authError && <p className="hmrc-auth__error-message-text">Failed to start HMRC login.</p>}
        </>
    );
}

export default HmrcAuth;
